import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle, ArrowLeft, Check, ChevronDown, Hash, Lock, RotateCcw, Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { useAuth } from '../../context/AuthContext';
import { DeliveryLine, DeliveryOrderDetail, LineStatus } from '../../types/stock.types';
import { deliveriesApi } from '../../utils/stock-api';
import { ApiError } from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { groupLinesByCategory, lineCategory } from '../../utils/stock-lines';

/**
 * The four states a line can be in, as filter pills.
 *
 * Colours are the ones Moe named for the console: Picked green, Unpicked grey, Short
 * yellow, Partially picked red — and the same four colours carry onto the line cards, so
 * a filtered list reads the same way as the pill that produced it.
 *
 * `label` is what fits on a phone; `name` is the full status, used for the accessible
 * name so nothing is abbreviated for screen readers.
 */
const LINE_FILTERS: {
    status: LineStatus; label: string; name: string; active: string; idle: string;
}[] = [
    {
        status: 'picked', label: 'Picked', name: 'Picked',
        active: 'bg-emerald-600 border-emerald-600 text-white',
        idle: 'border-emerald-300 bg-emerald-50 text-emerald-800',
    },
    {
        status: 'pending', label: 'Unpicked', name: 'Unpicked',
        active: 'bg-slate-600 border-slate-600 text-white',
        idle: 'border-slate-300 bg-slate-50 text-slate-700',
    },
    {
        status: 'shortfall', label: 'Short', name: 'Short',
        active: 'bg-amber-500 border-amber-500 text-white',
        idle: 'border-amber-300 bg-amber-50 text-amber-800',
    },
    {
        status: 'partially_picked', label: 'Partial', name: 'Partially picked',
        active: 'bg-red-600 border-red-600 text-white',
        idle: 'border-red-300 bg-red-50 text-red-700',
    },
];

/**
 * Pick sheet — STOCK.md §4, ported from gearlist-poc's PickSheetPage.
 *
 * Mobile-first and deliberately sparse: this is used one-handed, outdoors, often in
 * gloves. Big tap targets, a numeric keypad for quantities, and no controls that belong
 * to a manager — approval and assignment live on the detail page.
 *
 * The crew PICK from this sheet; they do not change what is on it. There is no add-item
 * action here: a line missing from the load is added to the order on the delivery page,
 * by whoever is allowed to change its material list, and then picked like any other.
 * What the picker records is what they found — a count, a shortfall with a reason, or a
 * partial load — never a new line of their own.
 *
 * TICK-TO-CLAIM. Tapping a line claims it and locks it from the other pickers on the
 * sheet. The lock is decided by the server, so two people tapping the same line at the
 * same moment is safe: one wins and the other is told who got it. A claim conflict
 * refreshes the sheet rather than just erroring, because the useful next thing is
 * seeing the current state.
 *
 * THE LOCK OPENS ONCE THE LINE IS A PARTIAL OR A SHORTFALL. A claim only protects work
 * in progress: the moment a line carries a partial count or a shortfall it is open to
 * the whole crew, so whoever finds the rest of the load can finish the count or correct
 * the reason. A PICKED line stays with whoever recorded it — it is finished evidence.
 * `pickedBy` still names the original claimer on every one of these lines.
 */
export default function PickSheetPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_DELIVERY_ORDER);
    const { id, yardId } = useParams<{ id: string; yardId?: string }>();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const detailHref = yardId
        ? `/stock/yards/${yardId}/deliveries/${id}`
        : `/stock/deliveries/${id}`;

    const [order, setOrder] = useState<DeliveryOrderDetail | null>(null);
    /** Server-side refusal (yard scoping) — rendered as a page, not eternal skeletons (UAT B9). */
    const [accessDenied, setAccessDenied] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<number | null>(null);

    const [shortfallLine, setShortfallLine] = useState<DeliveryLine | null>(null);
    const [shortfallQty, setShortfallQty] = useState('');
    const [shortfallReason, setShortfallReason] = useState('');

    /** The line whose picked quantity is being entered (the "Qty" action). */
    const [qtyLine, setQtyLine] = useState<DeliveryLine | null>(null);
    const [qtyValue, setQtyValue] = useState('');
    /** Status filters for the list. Empty means nothing is filtered out. */
    const [filters, setFilters] = useState<LineStatus[]>([]);
    /**
     * Category filters, from the categories actually on this sheet (a product's Product
     * Library category). Additive with the status pills: nothing ticked means every
     * category, and the two kinds of filter combine.
     */
    const [categoryFilters, setCategoryFilters] = useState<string[]>([]);

    // Background refreshes (after a pick/reset/release) swap the order data in place so
    // the list never unmounts — replacing it with skeletons would clamp the scroll back
    // to the top and make the picker hunt for their place on long sheets.
    const load = useCallback(async (background = false) => {
        if (!id) return;
        try {
            if (!background) setLoading(true);
            setOrder(await deliveriesApi.get(Number(id)));
            setAccessDenied(null);
        } catch (err) {
            // A 403 here is yard scoping, not a transient failure — show it on the page
            // (the server's own wording) instead of leaving skeletons up forever (UAT B9).
            if (err instanceof ApiError && err.status === 403) {
                setAccessDenied(err.data?.message || err.message);
                return;
            }
            toast.error(err instanceof Error ? err.message : 'Failed to load pick sheet');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    const run = async (lineId: number, fn: () => Promise<any>, message?: string) => {
        try {
            setBusy(lineId);
            await fn();
            if (message) toast.success(message);
            await load(true);
        } catch (err: any) {
            const msg = err?.data?.message || err?.message || 'Something went wrong';
            toast.error(msg);
            // A lost claim means the sheet moved on — show what it looks like now.
            if (err?.data?.claimedBy) await load(true);
        } finally {
            setBusy(null);
        }
    };

    if (!canRead) return <AccessRestricted />;

    if (accessDenied) {
        return <AccessRestricted accessMessage={accessDenied}
                                 backMessage="Back to Delivery" navigateTo={detailHref} />;
    }

    if (loading || !order) {
        return (
            <div className="space-y-3 animate-pulse p-2 md:p-0">
                {[0, 1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-300 rounded-md" />)}
            </div>
        );
    }

    // AuthContext types the user id as a string; stock records use numeric ids.
    // Normalise once here rather than coercing at every comparison below.
    const myId = currentUser?.id != null ? Number(currentUser.id) : null;
    const pickable = ['assigned', 'in_progress'].includes(order.status);

    /**
     * Finished with the line, as opposed to merely counted.
     *
     * A shortfall settles its line — the picker counted what is there and said why. A
     * PARTIAL pick does not: the count is short with nothing behind it, so it stays in the
     * "left to pick" figure and holds the pick open (the server refuses to finish while one
     * exists). Counting it as done here would promise a completion the API then refuses.
     */
    const done = order.lines.filter(
        (l) => l.lineStatus === 'picked' || l.lineStatus === 'shortfall').length;
    const total = order.lines.length;
    const allDone = total > 0 && done === total;
    const partialCount = order.lines.filter((l) => l.lineStatus === 'partially_picked').length;

    /**
     * The sheet in product-category sections, in the same order the delivery page shows
     * them — a picker who read "Cages" second there reads it second here.
     *
     * `allGroups` is every section and its size (the dropdown's options); `visibleGroups`
     * is what the filters leave, grouped the same way.
     */
    const allGroups = groupLinesByCategory(order.lines);

    /**
     * Filters are additive across both kinds: the status pills and the category ticks must
     * all pass. Nothing selected on either means the whole sheet.
     */
    const visibleLines = order.lines.filter((l) => {
        if (filters.length && !filters.includes(l.lineStatus)) return false;
        if (categoryFilters.length && !categoryFilters.includes(lineCategory(l))) return false;
        return true;
    });
    const visibleGroups = groupLinesByCategory(visibleLines);
    const filtersActive = filters.length > 0 || categoryFilters.length > 0;

    const toggleFilter = (status: LineStatus) =>
        setFilters((prev) => (prev.includes(status)
            ? prev.filter((s) => s !== status)
            : [...prev, status]));

    /**
     * Enter what was actually picked.
     *
     * All of it lands as `picked`; less than the ordered figure lands as
     * `partially_picked`. That is deliberately not the same as a shortfall: a shortfall
     * carries a reason and settles the line, while a partial keeps the sheet open until
     * the rest is loaded or the line is recorded short.
     */
    const openQty = (line: DeliveryLine) => {
        setQtyLine(line);
        setQtyValue(String(line.qtyPicked ?? line.quantity));
    };

    const saveQty = async () => {
        if (!qtyLine) return;
        const ordered = Number(qtyLine.quantity);
        const picked = Number(qtyValue);
        // Whole numbers only. The dialog is outside a form, so the browser's own step
        // validation never runs here — and the API rejects fractions anyway.
        if (!Number.isInteger(picked) || picked <= 0) {
            toast.error('Enter how many were picked — a whole number above zero.');
            return;
        }
        if (picked > ordered) {
            toast.error(`That is more than was ordered — this line is for ${ordered}.`);
            return;
        }
        const lineStatus: LineStatus = picked >= ordered ? 'picked' : 'partially_picked';
        const line = qtyLine;
        setQtyLine(null);
        await run(line.id, () => deliveriesApi.pickLine(order.id, line.id, {
            lineStatus, qtyPicked: picked,
        }), lineStatus === 'picked'
            ? 'Line picked'
            : `${ordered - picked} still to load — the pick stays open until it is complete or short`);
    };

    const openShortfall = (line: DeliveryLine) => {
        setShortfallLine(line);
        setShortfallQty(String(line.qtyPicked ?? 0));
        setShortfallReason(line.shortfallReason ?? '');
    };

    /** Whole numbers only, for the quantity dialog (see saveQty). */
    const qtyPickedOk = Number.isInteger(Number(qtyValue)) && Number(qtyValue) > 0;

    return (
        <div className="space-y-4 p-2 md:p-0 max-w-3xl">
            <div>
                <Button variant="ghost" size="sm" onClick={() => navigate(detailHref)} className="mb-2 -ml-2">
                    <ArrowLeft size={16} className="mr-1.5" />Delivery
                </Button>
                <div>
                    <h1 className="text-xl font-bold font-mono">{order.orderNumber}</h1>
                    <p className="text-sm text-muted-foreground">{order.project?.projectName}</p>
                </div>
            </div>

            {/*
              Progress, pinned.

              Picking happens standing at the rack with the sheet scrolled to wherever the
              gear is, and the one thing the picker needs to keep answering is "how much is
              left" — a count that scrolls off the top is a count they have to hunt for.

              The offsets are arithmetic against the shell, measured rather than guessed:
              the scroll container is <main>, which carries pt-[4.5rem] (72px) to clear the
              fixed 56px app header, and a sticky box pins at `top + that padding`. So
              -top-4 (-16px) lands it flush under the header on mobile. At lg the header is
              hidden and main's padding is 24px, so top-0 pins it on the content-start line.

              The header above keeps the order number and the back link, so the count lives
              here alone rather than in two places.
            */}
            <div className={cn(
                'sticky -top-4 lg:top-0 z-30 -mx-2 md:mx-0 px-2 md:px-0 py-2',
                'bg-background border-b border-border'
            )}>
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-muted-foreground">
                        {total === 0
                            ? 'Nothing to pick yet'
                            : (allDone ? 'Every line picked' : `${total - done} left to pick`)}
                    </span>
                    <span
                        className={cn('text-lg font-semibold tabular-nums',
                            allDone ? 'text-emerald-700' : 'text-foreground')}
                        aria-label={`${done} of ${total} lines picked`}
                    >
                        {done}/{total}
                    </span>
                </div>
                <div
                    role="progressbar"
                    aria-valuenow={done}
                    aria-valuemin={0}
                    aria-valuemax={total}
                    aria-label={`${done} of ${total} lines picked`}
                    className="mt-1.5 h-1.5 w-full rounded-full bg-muted overflow-hidden"
                >
                    <div
                        className={cn('h-full rounded-full transition-[width] duration-300',
                            allDone ? 'bg-emerald-600' : partialCount > 0 ? 'bg-red-600' : 'bg-primary')}
                        style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }}
                    />
                </div>

                {/*
                  Filters, inside the pinned block so they travel with the count — a
                  filter you have to scroll up to change is one you stop using. Additive:
                  each pill toggles on its own, none toggled means the whole sheet.
                  Counts are the pill's own population, not the filtered remainder, so the
                  number on a pill always means the same thing.
                */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {LINE_FILTERS.map((f) => {
                        const count = order.lines.filter((l) => l.lineStatus === f.status).length;
                        const on = filters.includes(f.status);
                        return (
                            <button
                                key={f.status}
                                type="button"
                                aria-pressed={on}
                                aria-label={`${f.name}, ${count} line${count === 1 ? '' : 's'}`}
                                onClick={() => toggleFilter(f.status)}
                                className={cn(
                                    'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
                                    'text-xs font-medium tabular-nums transition-colors',
                                    on ? f.active : f.idle
                                )}
                            >
                                {f.label}
                                <span className="font-semibold">{count}</span>
                            </button>
                        );
                    })}
                    {/*
                      Category. The fourth pill is not a status — it opens the categories
                      actually on this sheet (each product's Product Library category) so
                      a picker walking one section of the rack can hide the rest. Additive
                      with the status pills, and the menu stays open while ticking so two
                      or three sections can be chosen in one go.
                    */}
                    {allGroups.length > 0 && (
                        <DropdownMenu modal={false}>
                            {/* Non-modal on purpose: this is a filter, not a command — the
                                sheet stays visible and scrollable so a picker watches the
                                sections drop out as they tick. */}
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    aria-label={categoryFilters.length
                                        ? `Category, ${categoryFilters.length} selected`
                                        : 'Category'}
                                    className={cn(
                                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
                                        'text-xs font-medium tabular-nums transition-colors',
                                        categoryFilters.length
                                            ? 'bg-slate-600 border-slate-600 text-white'
                                            : 'border-slate-300 bg-white text-slate-700'
                                    )}
                                >
                                    Category
                                    {categoryFilters.length > 0 && (
                                        <span className="font-semibold">{categoryFilters.length}</span>
                                    )}
                                    <ChevronDown size={12} />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                                <DropdownMenuLabel>Filter by category</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {allGroups.map((g) => (
                                    <DropdownMenuCheckboxItem
                                        key={g.category}
                                        checked={categoryFilters.includes(g.category)}
                                        // Radix closes a menu on select; a filter is not a
                                        // command, so the menu stays up for the next tick.
                                        onSelect={(e) => e.preventDefault()}
                                        onCheckedChange={() => setCategoryFilters((prev) =>
                                            prev.includes(g.category)
                                                ? prev.filter((c) => c !== g.category)
                                                : [...prev, g.category])}
                                    >
                                        <span className="flex-1 truncate">{g.category}</span>
                                        <span className="text-xs text-muted-foreground tabular-nums">
                                            {g.lines.length}
                                        </span>
                                    </DropdownMenuCheckboxItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    {filtersActive && (
                        <button type="button"
                                onClick={() => { setFilters([]); setCategoryFilters([]); }}
                                className="px-1 text-xs text-muted-foreground underline underline-offset-2">
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {!pickable && (
                <Card className="border-amber-200 bg-amber-50/50">
                    <CardContent className="p-4 text-sm text-amber-800">
                        This delivery is {order.status.replace(/_/g, ' ')} — picking is closed.
                    </CardContent>
                </Card>
            )}

            {pickable && !order.isAssignedToMe && (
                <Card className="border-amber-200 bg-amber-50/50">
                    <CardContent className="p-4 text-sm text-amber-800">
                        You are not on this pick crew. Ask a yard manager to add you before claiming lines.
                    </CardContent>
                </Card>
            )}

            {allDone && pickable && (
                <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardContent className="p-4 text-sm flex items-center justify-between gap-3">
                        <span className="text-emerald-800">
                            Every line is done. Photos next.
                        </span>
                        <Button size="sm" onClick={() => navigate(detailHref)}>
                            Go to photos
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Why the pick cannot be finished yet. The server refuses the transition, so
                saying it here is the difference between a rule and a mystery. */}
            {pickable && partialCount > 0 && (
                <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="p-4 text-sm text-red-800">
                        {partialCount} line{partialCount === 1 ? ' is' : 's are'} only partially
                        picked. Load the rest, or record a shortfall with a reason — the pick
                        cannot be completed while a partial count is on the sheet.
                    </CardContent>
                </Card>
            )}

            {/* Lines — big targets, one row each, under their product category. The
                heading is the Product Library's category, so the sheet reads as sections
                instead of one long list. */}
            <div className="space-y-4">
                {visibleGroups.map((group) => (
                    <div key={group.category} className="space-y-2">
                        <div className="flex items-baseline justify-between gap-2 px-0.5">
                            <h2 className="text-sm font-semibold">{group.category}</h2>
                            <span className="text-xs text-muted-foreground tabular-nums">
                                {group.lines.length} line{group.lines.length === 1 ? '' : 's'}
                            </span>
                        </div>
                {group.lines.map((line) => {
                    const claimedByOther = Boolean(line.pickedBy && line.pickedBy.id !== myId);
                    const isPicked = line.lineStatus === 'picked';
                    const isShortfall = line.lineStatus === 'shortfall';
                    const isPartial = line.lineStatus === 'partially_picked';
                    // Only an in-progress claim locks a line, and only against the rest of
                    // the crew. A partial or a shortfall is open work: anyone can finish
                    // the count or correct the reason (the API enforces the same rule). A
                    // picked line is done and stays with its picker.
                    const lockedByOther = claimedByOther && !isPartial && !isShortfall;
                    const working = busy === line.id;
                    const outstanding = Number(line.quantity) - Number(line.qtyPicked ?? 0);

                    return (
                        <Card key={line.id} className={cn(
                            'transition-colors',
                            isPicked && 'border-emerald-300 bg-emerald-50/40',
                            isShortfall && 'border-amber-300 bg-amber-50/40',
                            isPartial && 'border-red-300 bg-red-50/40',
                            lockedByOther && 'opacity-60'
                        )}>
                            <CardContent className="p-3">
                                <div className="flex items-start gap-3">
                                    {/* Tap target — the whole tick area, not a small checkbox.
                                        On a partial line it means "the rest turned up": the
                                        tick completes the line at the ordered figure. */}
                                    <button
                                        type="button"
                                        aria-label={isPicked
                                            ? 'Undo pick'
                                            : isPartial
                                                ? `Pick the rest of ${line.product?.name}`
                                                : `Pick ${line.product?.name}`}
                                        disabled={!pickable || !order.isAssignedToMe || lockedByOther || working}
                                        className={cn(
                                            'h-12 w-12 shrink-0 rounded-lg border flex items-center justify-center',
                                            isPicked
                                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                                : isPartial
                                                    ? 'border-red-300 text-red-600 hover:bg-red-50'
                                                    : 'border-input hover:bg-accent',
                                            (!pickable || lockedByOther || !order.isAssignedToMe) && 'opacity-50'
                                        )}
                                        onClick={() => {
                                            if (isPicked) {
                                                run(line.id, () => deliveriesApi.pickLine(order.id, line.id, {
                                                    lineStatus: 'pending', qtyPicked: 0,
                                                }));
                                                return;
                                            }
                                            run(line.id, () => deliveriesApi.pickLine(order.id, line.id, {
                                                lineStatus: 'picked', qtyPicked: line.quantity,
                                            }));
                                        }}
                                    >
                                        {lockedByOther && !isPicked
                                            ? <Lock size={18} />
                                            : <Check size={22} />}
                                    </button>

                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium leading-tight">{line.product?.name}</p>
                                        <p className="text-xs text-muted-foreground font-mono mt-0.5">
                                            {line.product?.sku}
                                        </p>
                                        {lockedByOther && (
                                            <p className="text-xs text-amber-700 mt-1">
                                                Claimed by {line.pickedBy?.name}
                                            </p>
                                        )}
                                        {isShortfall && line.shortfallReason && (
                                            <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                                                <AlertTriangle size={11} />{line.shortfallReason}
                                            </p>
                                        )}
                                        {isPartial && (
                                            <p className="text-xs text-red-700 mt-1">
                                                Partially picked — {outstanding} still to load
                                            </p>
                                        )}
                                    </div>

                                    <div className="text-right shrink-0">
                                        <p className={cn('text-lg font-semibold tabular-nums leading-none',
                                            isPartial && 'text-red-700')}>
                                            {line.qtyPicked ?? line.quantity}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            of {line.quantity} {line.product?.uom}
                                        </p>
                                    </div>
                                </div>

                                {/* Secondary actions — shown unless the line is locked to another
                                    picker. A partial or a shortfall is never locked, so the
                                    whole crew can re-count it or correct the reason. */}
                                {pickable && order.isAssignedToMe && !lockedByOther && (
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <Button variant="outline" size="sm" disabled={working}
                                                onClick={() => openQty(line)}>
                                            <Hash size={14} className="mr-1.5" />Qty
                                        </Button>
                                        <Button variant="outline" size="sm" disabled={working}
                                                onClick={() => openShortfall(line)}>
                                            <AlertTriangle size={14} className="mr-1.5" />
                                            {isShortfall ? 'Edit shortfall' : 'Short'}
                                        </Button>
                                        {line.pickedBy?.id === myId && line.lineStatus === 'pending' && (
                                            <Button variant="ghost" size="sm" disabled={working}
                                                    onClick={() => run(line.id,
                                                        () => deliveriesApi.unclaimLine(order.id, line.id),
                                                        'Released')}>
                                                <Undo2 size={14} className="mr-1.5" />Release
                                            </Button>
                                        )}
                                        {(isPicked || isShortfall || isPartial) && (
                                            <Button variant="ghost" size="sm" disabled={working}
                                                    onClick={() => run(line.id,
                                                        () => deliveriesApi.pickLine(order.id, line.id, {
                                                            lineStatus: 'pending', qtyPicked: 0,
                                                        }), 'Reset')}>
                                                <RotateCcw size={14} className="mr-1.5" />Reset
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
                    </div>
                ))}
            </div>

            {order.lines.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                    Nothing on this delivery to pick yet.
                </p>
            )}

            {order.lines.length > 0 && visibleLines.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                    No lines match the filters you picked.
                </p>
            )}

            {/*
              Enter the quantity picked.

              The tick is the fast path for "it was all there"; this is for when it was not.
              Less than ordered lands as `partially_picked`, which is not a shortfall: a
              shortfall carries a reason and settles the line, while this keeps the pick open
              until the rest is loaded or the line is recorded short.
            */}
            <Dialog open={Boolean(qtyLine)} onOpenChange={(o) => { if (!o) setQtyLine(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Quantity picked — {qtyLine?.product?.name}</DialogTitle>
                        <DialogDescription>
                            How many did you actually load? Anything less than the{' '}
                            {qtyLine?.quantity} {qtyLine?.product?.uom} ordered leaves the line
                            partially picked, and a partial line keeps the pick open — the sheet
                            cannot be finished until it is complete or recorded short.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="qty-picked">
                            Quantity picked (ordered {qtyLine?.quantity})
                        </Label>
                        <Input id="qty-picked" type="number" inputMode="numeric" min="1"
                               value={qtyValue} onChange={(e) => setQtyValue(e.target.value)} />
                        {qtyLine && Number(qtyValue) > 0 && Number(qtyValue) < Number(qtyLine.quantity) && (
                            <p className="text-xs text-red-700 mt-1">
                                Partial pick — {Number(qtyLine.quantity) - Number(qtyValue)} still to
                                load. Record a shortfall instead if this is all there is.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setQtyLine(null)}>Cancel</Button>
                        <Button disabled={busy !== null || !qtyPickedOk}
                                onClick={() => void saveQty()}>
                            Save quantity
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Shortfall */}
            <Dialog open={Boolean(shortfallLine)} onOpenChange={(o) => { if (!o) setShortfallLine(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Shortfall — {shortfallLine?.product?.name}</DialogTitle>
                        <DialogDescription>
                            Record what you actually found and why it's short. The reason is what makes
                            this useful to whoever picks it up next.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="sf-qty">
                                Quantity picked (ordered {shortfallLine?.quantity})
                            </Label>
                            <Input id="sf-qty" type="number" inputMode="numeric" min="0"
                                   value={shortfallQty} onChange={(e) => setShortfallQty(e.target.value)} />
                        </div>
                        <div>
                            <Label htmlFor="sf-reason">Reason *</Label>
                            <Textarea id="sf-reason" rows={3} value={shortfallReason}
                                      onChange={(e) => setShortfallReason(e.target.value)}
                                      placeholder="e.g. Only 8 left in the rack" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShortfallLine(null)}>Cancel</Button>
                        <Button disabled={!shortfallReason.trim() || busy !== null}
                                onClick={async () => {
                                    if (!shortfallLine) return;
                                    await run(shortfallLine.id, () => deliveriesApi.pickLine(
                                        order.id, shortfallLine.id, {
                                            lineStatus: 'shortfall',
                                            qtyPicked: Number(shortfallQty) || 0,
                                            shortfallReason: shortfallReason.trim(),
                                        }), 'Shortfall recorded');
                                    setShortfallLine(null);
                                }}>
                            Record shortfall
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
